import { SimWorld, addLog } from '../types/SimWorld';
import { SimNPC } from '../types/SimNPC';
import { GameMap, MapTile, TileType } from '../types/GameMap';
import { updateRelationship } from '../systems/RelationshipManager';
import { tryCardExchange } from '../systems/CardExchangeManager';

/**
 * 同じマスにいるNPC間の交流をチェック
 */
export function checkLocationInteractions(map: GameMap, world: SimWorld): void {
  for (let y = 0; y < map.height; y++) {
    for (let x = 0; x < map.width; x++) {
      const tile = map.tiles[y][x];

      if (tile.npcs.length >= 2) {
        processLocationInteraction(tile, world);
      }
    }
  }
}

/**
 * 場所での交流処理
 */
function processLocationInteraction(tile: MapTile, world: SimWorld): void {
  const npcIds = tile.npcs;
  const npcs = world.npcs.filter((n) => npcIds.includes(n.id) && n.isAlive);

  if (npcs.length < 2) return;

  // 2人の場合
  if (npcs.length === 2) {
    const [npc1, npc2] = npcs;
    const interactionChance = calculateInteractionChance(npc1, npc2, tile);

    if (Math.random() < interactionChance) {
      // 友好的交流（同じ場所で出会うと親密度UP）
      const affectionGain = Math.floor(Math.random() * 12) + 10; // 10-21
      updateRelationship(npc1, npc2, affectionGain, world.day);

      // 町では高確率でログ表示
      if (tile.type === 'town' || Math.random() < 0.15) {
        addLog(
          world,
          `💬 ${npc1.name}と${npc2.name}が${getTileNameJP(tile.type)}で出会った`
        );
      }

      // カード交換を試行（好感度が十分なら）
      tryCardExchange(npc1, npc2, world);
    }
  }

  // 3人以上の場合（グループ交流）
  if (npcs.length >= 3) {
    for (let i = 0; i < npcs.length; i++) {
      for (let j = i + 1; j < npcs.length; j++) {
        if (Math.random() < 0.3) {
          const affectionGain = Math.floor(Math.random() * 8) + 5; // 5-12
          updateRelationship(npcs[i], npcs[j], affectionGain, world.day);

          // カード交換を試行（好感度が十分なら）
          tryCardExchange(npcs[i], npcs[j], world);
        }
      }
    }

    if (Math.random() < 0.1) {
      addLog(
        world,
        `🎉 ${getTileNameJP(tile.type)}で${npcs.length}人が集まった`
      );
    }
  }
}

/**
 * 交流確率計算
 */
function calculateInteractionChance(
  npc1: SimNPC,
  npc2: SimNPC,
  tile: MapTile
): number {
  let chance = 0.5; // ベース50%

  // 町では交流しやすい
  if (tile.type === 'town') {
    chance += 0.3;
  }

  // 既存の関係性で変動
  const relationship = npc1.relationships.get(npc2.id);
  if (relationship) {
    chance += relationship.affection / 200; // 好感度100で+0.5
  }

  return Math.min(1.0, chance);
}

/**
 * タイル名（日本語）
 */
function getTileNameJP(type: TileType): string {
  const names: Record<TileType, string> = {
    grass: '草原',
    forest: '森',
    water: '水辺',
    food_spot: '食料スポット',
    town: '町',
    mountain: '山',
    fire_zone: '炎の領域',
    water_zone: '水の領域',
    light_zone: '光の領域',
    dark_zone: '闇の領域',
    primitive_zone: '原始の領域',
    future_zone: '未来の領域',
    forbidden_zone: '禁忌の地',
  };
  return names[type];
}
